import { useEffect, useMemo, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CheckCircle, Circle, FileCsv, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/AppShell";
import { PayrollCalcCard } from "@/components/PayrollCalcCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePortfolio } from "@/context/ProfileContext";
import { useAuth } from "@/context/AuthContext";
import { exportCsv, exportPdf, matchShortfalls } from "@/lib/ledgerExport";
import { api, errMsg, useFetch } from "@/lib/api";
import { buildLedger } from "@/lib/paychecks";
import { usd, compact, changeClass } from "@/lib/format";

const THIS_YEAR = new Date().getFullYear();

function Stat({ label, value, testid, tone = "" }) {
  return <div className="glass p-4"><p className="text-xs text-muted-foreground">{label}</p><p className={`font-mono-num text-xl font-semibold mt-1 ${tone}`} data-testid={testid}>{value}</p></div>;
}

function MoneyCell({ value, placeholder, onSave, testid }) {
  return (
    <input type="number" step="0.01" min="0" defaultValue={value ?? ""} placeholder={placeholder} key={`${testid}-${value}`}
      onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== value) onSave(v); }}
      className="w-24 bg-transparent text-right font-mono-num rounded px-1 py-0.5 placeholder:text-slate-400 hover:bg-slate-900/5 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-1 focus:ring-cyan-500" data-testid={testid} />
  );
}

function Timeline({ rows, statements }) {
  const data = rows.map((r) => ({ date: r.date.slice(5), Employee: Math.round(r.employee), "Agency 1%": Math.round(r.auto), "Agency match": Math.round(r.match), "Cumulative total": Math.round(r.cumEmp + r.cumAgency) }));
  return (
    <div className="h-80" data-testid="paycheck-timeline-chart">
      <ResponsiveContainer>
        <ComposedChart data={data}>
          <CartesianGrid vertical={false} strokeOpacity={0.25} />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94A3B8" minTickGap={12} />
          <YAxis yAxisId="l" tickFormatter={compact} tick={{ fontSize: 11 }} width={56} stroke="#94A3B8" />
          <YAxis yAxisId="r" orientation="right" tickFormatter={compact} tick={{ fontSize: 11 }} width={56} stroke="#94A3B8" />
          <Tooltip formatter={(v) => usd(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="l" dataKey="Employee" stackId="a" fill="#0891B2" />
          <Bar yAxisId="l" dataKey="Agency 1%" stackId="a" fill="#94A3B8" />
          <Bar yAxisId="l" dataKey="Agency match" stackId="a" fill="#10B981" radius={[3, 3, 0, 0]} />
          <Line yAxisId="r" dataKey="Cumulative total" stroke="#6366F1" strokeWidth={2} dot={false} />
          {statements.map((s) => <ReferenceLine key={s.date} yAxisId="l" x={s.date.slice(5)} stroke="#F59E0B" strokeDasharray="4 3" label={{ value: "Stmt", fontSize: 10, fill: "#F59E0B", position: "top" }} />)}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function StatementCheck({ rows, statements }) {
  if (!statements.length) return <p className="text-sm text-muted-foreground" data-testid="statement-check-empty">Import a TSP statement on Accounts & Sync to compare its contribution totals against this ledger.</p>;
  return (
    <table className="w-full text-sm" data-testid="statement-check-table">
      <thead className="text-xs text-muted-foreground"><tr><th className="text-left py-2 font-medium">Statement date</th><th className="text-right font-medium">Ledger employee YTD</th><th className="text-right font-medium">Statement employee</th><th className="text-right font-medium">Ledger agency YTD</th><th className="text-right font-medium">Statement agency</th><th className="text-right font-medium">Agency gap</th></tr></thead>
      <tbody>{statements.map((s) => {
        const upTo = rows.filter((r) => r.date <= s.date).pop();
        const le = upTo?.cumEmp || 0, la = upTo?.cumAgency || 0;
        const gap = s.agency_match != null ? s.agency_match - la : null;
        return (
          <tr key={s.date} className="border-t border-border/60 font-mono-num" data-testid={`statement-check-${s.date}`}>
            <td className="py-2">{s.date}</td><td className="text-right">{usd(le, 2)}</td><td className="text-right">{s.employee_contrib != null ? usd(s.employee_contrib, 2) : "—"}</td>
            <td className="text-right">{usd(la, 2)}</td><td className="text-right">{s.agency_match != null ? usd(s.agency_match, 2) : "—"}</td>
            <td className={`text-right ${gap == null ? "" : Math.abs(gap) < 1 ? "text-emerald-600" : changeClass(gap)}`}>{gap == null ? "—" : Math.abs(gap) < 1 ? "Match" : usd(gap, 2)}</td>
          </tr>
        );
      })}</tbody>
    </table>
  );
}

export default function Paychecks() {
  const { profile, saveProfile } = usePortfolio();
  const { user } = useAuth();
  const [year, setYear] = useState(THIS_YEAR);
  const [anchor, setAnchor] = useState("");
  useEffect(() => { if (profile) setAnchor(profile.first_pay_date || ""); }, [profile]);
  const { data, reload } = useFetch(`/paychecks?year=${year}`);
  const rows = useMemo(() => (profile ? buildLedger(profile, year, data?.overrides) : []), [profile, year, data]);
  if (!profile) return <p className="text-sm text-muted-foreground">Loading...</p>;
  const save = async (r, patch) => {
    try {
      await api.put(`/paychecks/${r.date}`, { actual_employee: r.actual_employee, actual_agency: r.actual_agency, verified: r.verified, note: r.note, ...patch });
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveAnchor = async () => {
    try { await saveProfile({ ...profile, first_pay_date: anchor || null }); toast.success("Pay schedule updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const last = rows[rows.length - 1] || {};
  const today = new Date().toISOString().slice(0, 10);
  const ytd = rows.filter((r) => r.date <= today).pop() || { cumEmp: 0, cumAgency: 0 };
  const verified = rows.filter((r) => r.verified).length;
  const variance = rows.reduce((s, r) => s + r.variance, 0);
  const statements = data?.statements || [];
  const shortSet = new Set(matchShortfalls(rows).map((r) => r.date));
  const totals = { employee: last.cumEmp || 0, agency: last.cumAgency || 0, verified, variance };
  return (
    <div>
      <PageHeader title="Paycheck Ledger" subtitle="Every paycheck's expected employee and agency TSP contributions. Enter what your LES or statement shows and tick it off to verify."
        actions={<>
          <Button variant="outline" className="rounded-full gap-2" onClick={() => exportCsv(rows, year)} data-testid="ledger-export-csv"><FileCsv size={16} /> CSV</Button>
          <Button variant="outline" className="rounded-full gap-2" onClick={() => exportPdf(rows, year, { name: user?.name, profile, totals })} data-testid="ledger-export-pdf"><FilePdf size={16} /> PDF</Button>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="ledger-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[THIS_YEAR - 2, THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </>} />
      <PayrollCalcCard testid="payroll-calc-card-ledger" />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat label={`Employee YTD (${year === THIS_YEAR ? "to date" : "full year"})`} value={usd(year === THIS_YEAR ? ytd.cumEmp : last.cumEmp)} testid="ledger-ytd-employee" />
        <Stat label="Agency YTD (1% + match)" value={usd(year === THIS_YEAR ? ytd.cumAgency : last.cumAgency)} testid="ledger-ytd-agency" />
        <Stat label="Projected full year" value={usd((last.cumEmp || 0) + (last.cumAgency || 0))} testid="ledger-full-year" tone="text-grad-money" />
        <Stat label="Paychecks verified" value={`${verified} / ${rows.length}`} testid="ledger-verified-count" />
        <Stat label="Variance vs expected" value={usd(variance, 2)} testid="ledger-variance" tone={changeClass(variance)} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Panel title="Contribution timeline" className="xl:col-span-2" testid="panel-ledger-timeline"><Timeline rows={rows} statements={statements} /></Panel>
        <Panel title="Pay schedule" testid="panel-pay-schedule">
          <p className="text-sm text-muted-foreground mb-4">{rows.length} {profile.pay_frequency} paychecks in {year} · gross {usd(rows[0]?.gross, 2)} each · {profile.retirement_system} match rules.</p>
          {profile.pay_frequency === "biweekly" && (
            <div className="space-y-1.5">
              <Label htmlFor="anchor">A known pay date (anchors the bi-weekly cycle)</Label>
              <div className="flex gap-2"><Input id="anchor" type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)} data-testid="ledger-anchor-input" /><Button onClick={saveAnchor} data-testid="ledger-anchor-save">Save</Button></div>
            </div>
          )}
          {rows.some((r) => r.capped) && <p className="text-xs text-amber-700 dark:text-amber-400 mt-4" data-testid="ledger-cap-warning">You hit the IRS elective limit this year; later paychecks are reduced and agency match stops with them.</p>}
          <p className="text-xs text-muted-foreground mt-4">Salary, contribution rate and Roth split come from Profile & Pay.</p>
        </Panel>
        <Panel title="Statement reconciliation" className="xl:col-span-3" testid="panel-statement-check"><StatementCheck rows={rows} statements={statements} /></Panel>
        <Panel title="Paychecks" className="xl:col-span-3" testid="panel-ledger-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead className="text-xs text-muted-foreground border-b border-border"><tr>
                <th className="text-left py-2 font-medium">#</th><th className="text-left font-medium">Pay date</th><th className="text-right font-medium">Gross</th><th className="text-right font-medium">Traditional</th><th className="text-right font-medium">Roth</th><th className="text-right font-medium">Agency 1%</th><th className="text-right font-medium">Match</th><th className="text-right font-medium">Actual you</th><th className="text-right font-medium">Actual agency</th><th className="text-right font-medium">Cumulative</th><th className="text-center font-medium">Verified</th>
              </tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.date} className={`border-b border-border/60 font-mono-num ${r.date > today ? "opacity-60" : ""} ${r.verified ? (shortSet.has(r.date) ? "bg-rose-500/10" : "bg-emerald-500/5") : ""}`} data-testid={`ledger-row-${r.date}`}>
                  <td className="py-1.5 text-muted-foreground">{r.n}</td><td>{r.date}</td><td className="text-right">{usd(r.gross, 2)}</td>
                  <td className="text-right">{usd(r.trad, 2)}</td><td className="text-right">{usd(r.roth, 2)}</td><td className="text-right">{usd(r.auto, 2)}</td><td className="text-right">{usd(r.match, 2)}</td>
                  <td className="text-right"><MoneyCell value={r.actual_employee} placeholder={r.employee.toFixed(2)} onSave={(v) => save(r, { actual_employee: v })} testid={`ledger-actual-emp-${r.date}`} /></td>
                  <td className="text-right"><MoneyCell value={r.actual_agency} placeholder={r.agency.toFixed(2)} onSave={(v) => save(r, { actual_agency: v })} testid={`ledger-actual-agency-${r.date}`} /></td>
                  <td className="text-right">{usd(r.cumEmp + r.cumAgency)}</td>
                  <td className="text-center"><Button variant="ghost" size="icon" onClick={() => save(r, { verified: !r.verified })} aria-label="Toggle verified" data-testid={`ledger-verify-${r.date}`}>{r.verified ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <Circle size={18} />}</Button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}
